import { Avatar, Badge, Button, Dropdown, Space, Typography } from "antd";
import { Bell, LogOut, MenuIcon, User } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useApp } from "~/store";
import { getClientName } from "~/utils";
import { notificationsAPI } from "~/services/notifications/notifications.api";
import dayjs from "dayjs";

const { Text } = Typography;
type Props = {};

export const StaffHeader: FC<Props> = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toggleCollapsed } = useApp();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread notification count
  useEffect(() => {
    if (!user?.id) return;

    const fetchUnreadCount = async () => {
      try {
        // Only count notifications from the last 14 days to match the notifications page filter
        const fourteenDaysAgo = dayjs().subtract(14, 'days').toISOString();
        const count = await notificationsAPI.getUnreadCount(user.id, fourteenDaysAgo);
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();

    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [user?.id]);

  const handleToggle = () => {
    toggleCollapsed();
  };

  return (
    <header className="bg-white border-b w-full shadow-sm sticky top-0 z-20">
      <div className="max-w-7xl mx-auto py-2 px-3 md:px-6">
        <div className="flex justify-between items-center h-10 md:h-12">
          <div className="flex items-center">
            <Button
              type="text"
              icon={<MenuIcon size={18} className="md:size-5" />}
              onClick={handleToggle}
              className="mr-2 md:mr-3 lg:hidden p-1 md:p-2"
              aria-label="Toggle Mobile Menu"
            />
            <Link to="/" className="flex items-center">
              <img
                src="/logo.png"
                alt="AHC Logo"
                className="h-5 w-5 md:h-7 md:w-7 mr-2"
              />
              <Text className="font-semibold text-xs md:text-base">
                <span className="hidden md:inline">Art Handler Collective</span>
                <span className="md:hidden">AHC</span>
              </Text>
            </Link>
          </div>
          <div className="flex items-center space-x-1 md:space-x-3">
            <Link to="/notifications">
              <Badge count={unreadCount} className="cursor-pointer">
                <Button
                  type="text"
                  icon={<Bell size={16} className="md:size-4 lg:size-5" />}
                  className="p-1 md:p-2"
                />
              </Badge>
            </Link>
            <Dropdown
              menu={{
                items: [
                  {
                    key: "1",
                    label: <span>Profile</span>,
                    icon: <User size={16} />,
                    onClick: () => navigate("/profile"),
                  },
                  {
                    key: "2",
                    label: <span>Sign out</span>,
                    icon: <LogOut size={16} />,
                    danger: true,
                    onClick: logout,
                  },
                ],
              }}
              placement="bottomRight"
              arrow
            >
              <Space className="cursor-pointer">
                <Avatar
                  icon={<User size={14} />}
                  src={user?.staff?.avatar?.[0]?.url}
                />
                <Text className="text-xs md:text-sm hidden lg:inline-block">
                  {user && getClientName(user)}
                </Text>
              </Space>
            </Dropdown>
          </div>
        </div>
      </div>
    </header>
  );
};
